import React from 'react';
import gsap from 'gsap';
import { Container, Layout } from '@fiverr-private/layout_components';
import { Image } from '@fiverr-private/media';
import { Typography } from '@fiverr-private/typography';
import { appTranslate } from '../../i18n';
import { CARDS } from './cards';
import { SLOT, ANIM, DESIGN } from './constants';
import { cardContainer } from './styles.ve.css';

export interface DesktopCtx {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
    cardRefs: React.MutableRefObject<(HTMLElement | null)[]>;
    imageRefs: React.MutableRefObject<(HTMLImageElement | null)[][]>;
}

// Builds the scroll-scrubbed desktop timeline inside a gsap.matchMedia callback.
// All gsap.set initial states live here so matchMedia reverts them on resize.
export function buildDesktopTimeline(ctx: DesktopCtx) {
    const { wrapperRef, cardRefs, imageRefs } = ctx;

    if (!wrapperRef.current || !cardRefs.current[0]) {
        return;
    }

    const belowFold = DESIGN.height - SLOT.top;

    CARDS.forEach((_, i) => {
        const img0 = imageRefs.current[i]?.[0];
        const img1 = imageRefs.current[i]?.[1];
        // Each card waits one artboard-height below; card 0 sits in place. All visible —
        // the scroll-up (not opacity) drives the card-to-card transition.
        gsap.set(cardRefs.current[i], { yPercent: i === 0 ? 0 : 100, opacity: 1 });
        if (img0) {
            gsap.set(img0, { y: 0, scale: 1, opacity: 1 });
        }
        if (img1) {
            gsap.set(img1, { y: belowFold, scale: ANIM.incomingScale, opacity: 1 });
        }
    });

    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
        },
    });

    CARDS.forEach((_, i) => {
        const t = ANIM.hold + i * (ANIM.imageScroll + ANIM.cardScroll);
        const img0 = imageRefs.current[i]?.[0];
        const img1 = imageRefs.current[i]?.[1];

        if (img0) {
            tl.to(
                img0,
                {
                    y: -ANIM.outgoingRise,
                    scale: ANIM.outgoingScale,
                    opacity: 0,
                    ease: 'none',
                    duration: ANIM.imageScroll,
                },
                t
            );
        }
        if (img1) {
            tl.to(img1, { y: 0, scale: 1, ease: 'none', duration: ANIM.imageScroll }, t);
        }

        if (i < CARDS.length - 1) {
            // After the images swap, scroll the whole card up and the next card up into
            // its place — filmstrip style. Identical ease + duration keep the two cards
            // edge-adjacent (no seam gap/overlap mid-scroll).
            const scrollStart = t + ANIM.imageScroll;
            tl.to(cardRefs.current[i], { yPercent: -100, ease: 'none', duration: ANIM.cardScroll }, scrollStart);
            tl.to(cardRefs.current[i + 1], { yPercent: 0, ease: 'none', duration: ANIM.cardScroll }, scrollStart);
        }
    });
}

interface DesktopStageProps {
    scale: number;
    cardRefs: React.MutableRefObject<(HTMLElement | null)[]>;
    imageRefs: React.MutableRefObject<(HTMLImageElement | null)[][]>;
    hidden?: Record<string, boolean>;
}

export const DesktopStage: React.FC<DesktopStageProps> = ({ scale, cardRefs, imageRefs, hidden }) => (
    <Container
        hidden={hidden}
        position="absolute"
        top="50%"
        left="50%"
        width={`${DESIGN.width}px`}
        height={`${DESIGN.height}px`}
        overflow="hidden"
        transform={`translate(-50%, -50%) scale(${scale})`}
    >
        {CARDS.map(({ key, images }, i) => (
            <Layout
                key={key}
                className={cardContainer}
                ref={(el: HTMLElement | null) => {
                    cardRefs.current[i] = el;
                }}
            >
                <Typography
                    as="h2"
                    fontWeight="semibold"
                    color="black"
                    position="absolute"
                    left="52px"
                    top="367px"
                    width="1088px"
                    letterSpacing="-7.2px"
                    style={{ fontSize: '120px', lineHeight: '0.87', textTransform: 'uppercase' }}
                >
                    {appTranslate(`values.panels.${key}.heading`)}
                </Typography>

                <Typography
                    as="p"
                    fontWeight="light"
                    color="black"
                    position="absolute"
                    left="52px"
                    top="522px"
                    width="414px"
                    letterSpacing="-0.8px"
                    style={{ fontSize: '20px', lineHeight: '1.4' }}
                >
                    {appTranslate(`values.panels.${key}.body`)}
                </Typography>

                <Image
                    src={images[0]}
                    alt=""
                    objectFit="cover"
                    position="absolute"
                    left={SLOT.left}
                    top={SLOT.top}
                    width={SLOT.width}
                    height={SLOT.height}
                    borderRadius="3xl"
                    ref={(el) => {
                        imageRefs.current[i] ??= [];
                        imageRefs.current[i][0] = el;
                    }}
                />
                <Image
                    src={images[1]}
                    alt=""
                    objectFit="cover"
                    position="absolute"
                    left={SLOT.left}
                    top={SLOT.top}
                    width={SLOT.width}
                    height={SLOT.height}
                    borderRadius="3xl"
                    ref={(el) => {
                        imageRefs.current[i] ??= [];
                        imageRefs.current[i][1] = el;
                    }}
                />
            </Layout>
        ))}

        <Typography
            as="p"
            fontWeight="semibold"
            color="black"
            position="absolute"
            left="52px"
            right="52px"
            top="120px"
            letterSpacing="-1.44px"
            style={{ fontSize: '24px', lineHeight: '1.2', textTransform: 'uppercase' }}
        >
            {appTranslate('values.label')}
        </Typography>
    </Container>
);
